"use client";

import {
  Button as MantineButton,
  type ButtonProps as MantineButtonProps,
  type PolymorphicComponentProps,
} from "@mantine/core";
import { clsx } from "clsx";
import type { ReactNode } from "react";
import styles from "./Button.module.scss";
import {
  BUTTON_FILLED_VARIANT,
  BUTTON_SIZE,
  type ButtonFilledVariant,
  type ButtonSize,
} from "./button-tokens";

interface ButtonOwnProps
  extends Omit<MantineButtonProps, "variant" | "size" | "leftSection" | "rightSection" | "color"> {
  variant?: ButtonFilledVariant;
  buttonSize?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

// Polymorphic — defaults to a native <button>, but accepts `component={Link}` (+ `href`) to
// render as a navigation link styled as a button. Mirrors Mantine's own Button polymorphism.
export type ButtonProps<C = "button"> = PolymorphicComponentProps<C, ButtonOwnProps>;

/**
 * Filled/bordered container with a label and optional icons — 5 variants x 6 sizes.
 * Mirrors the Ultralight DS "Button" component (design_handoff_buttons/components/button.html).
 */
export function Button<C = "button">({
  variant = BUTTON_FILLED_VARIANT.Primary,
  buttonSize = BUTTON_SIZE.Size32,
  leftIcon,
  rightIcon,
  className,
  ...props
}: ButtonProps<C>) {
  return (
    // Two independently-generic polymorphic components (this wrapper's C, Mantine's own) can't
    // be chained through a prop spread — Mantine's Button reads `component`/`href` etc. off props
    // at runtime regardless of the type param used here, so this only loosens this internal
    // forwarding call, not the public ButtonProps<C>. Cast to the concrete (non-generic)
    // ButtonOwnProps rather than `any` — that still resolves the polymorphic mismatch but keeps
    // the hardcoded props below (variant, leftSection, ...) typo-checked.
    <MantineButton
      {...(props as ButtonOwnProps)}
      variant="default"
      className={clsx(styles.Button, className)}
      data-button-variant={variant}
      data-button-size={buttonSize}
      leftSection={leftIcon}
      rightSection={rightIcon}
    />
  );
}
